/**
 * 首页 · 三螺旋总览（常驻标签 · 默认落地页）
 *
 * 设计目标（沿用原型的产品判断）：
 *   企业家打开 App 的 3 秒内，必须看到「健康—企业—财富」是一个整体，
 *   首页不承担展示所有功能的职责，只承担建立三螺旋认知、并暴露最紧急的一件事。
 *
 * 与原型的差别：
 *   - 协同指数由后端用三条螺旋算出，不是写死的 84；
 *   - 任务勾选落库（乐观更新 + 失败回滚），切屏与刷新都不丢；
 *   - 消息红点来自真实未读数，不是 CSS 常亮装饰。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { GuardianTask, Overview } from '@hew3/shared';
import { resolveColor } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { charts } from '../../lib/charts.ts';
import { useAuth } from '../../store/auth.ts';
import { useUi } from '../../store/ui.ts';
import { AlertStrip, Avatar, Card, Chart, StateBox, TaskItem } from '../../components/ui.tsx';

export function HomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openSheet = useUi((s) => s.openSheet);
  const showToast = useUi((s) => s.showToast);
  const logout = useAuth((s) => s.logout);

  const overviewQuery = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<Overview>('/api/overview'),
  });

  const toggleTask = useMutation({
    mutationFn: (vars: { id: string; done: boolean }) =>
      api.patch<GuardianTask>(`/api/tasks/${vars.id}`, { done: vars.done }),
    // 乐观更新：勾选要立刻有反馈，不能等一次网络往返
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ['overview'] });
      const previous = queryClient.getQueryData<Overview>(['overview']);
      if (previous) {
        queryClient.setQueryData<Overview>(['overview'], {
          ...previous,
          tasks: previous.tasks.map((t) => (t.id === vars.id ? { ...t, done: vars.done } : t)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['overview'], context.previous);
      showToast('任务状态更新失败，请重试', 'error');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  if (overviewQuery.isLoading) {
    return (
      <div className="page-pad">
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 240 }} />
        <div className="skeleton skel-card" />
      </div>
    );
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    return (
      <StateBox
        kind="error"
        title="总览数据加载失败"
        desc="请检查网络或稍后重试。若持续失败，可退出后重新登录。"
        action={
          <div className="row-flex">
            <button type="button" className="btn sm" onClick={() => void overviewQuery.refetch()}>
              重新加载
            </button>
            <button type="button" className="btn sm" onClick={() => void logout()}>
              退出登录
            </button>
          </div>
        }
      />
    );
  }

  const data = overviewQuery.data;
  const { member, helix, health, topAlert, tasks } = data;
  const doneCount = tasks.filter((t) => t.done).length;
  const hour = new Date().getHours();
  const greeting = hour < 6 ? '凌晨好' : hour < 11 ? '早上好' : hour < 14 ? '中午好' : hour < 18 ? '下午好' : '晚上好';

  return (
    <div className="page">
      {/* ---------------- 欢迎头 ---------------- */}
      <div className="home-head">
        <Avatar char={member.avatarChar} gold={member.level === 'founding'} />
        <div className="hh-txt">
          <div className="hh-hi">
            持续守护 {member.guardMonths} 个月 · {member.city}
          </div>
          <div className="hh-name">
            {greeting}，{member.fullName}
            <span>{member.levelName}</span>
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          aria-label="消息中心"
          onClick={() => openSheet('messages')}
        >
          <svg viewBox="0 0 24 24">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.7 21a2 2 0 0 1-3.4 0" />
          </svg>
          {data.unreadMessages > 0 && <span className="nd" />}
        </button>
      </div>

      {/* ---------------- 三螺旋主视觉 ---------------- */}
      <div className="helix-card">
        <div className="helix-top">
          <div className="helix-tt">
            三螺旋协同演化引擎
            <small>HEALTH · ENTERPRISE · WEALTH</small>
          </div>
          <span className="badge h">协同指数 {helix.synergy}</span>
        </div>
        <div className="helix-svg-wrap">
          <Chart html={charts.helix({ h: helix.h, e: helix.e, w: helix.w, syn: helix.synergy })} />
        </div>
        <div className="helix-idx">
          <button type="button" className="hi h" onClick={() => navigate('/health')}>
            <div className="hi-l">生命健康 H</div>
            <div className="hi-v">
              {helix.h}
              <s>分</s>
            </div>
          </button>
          <button type="button" className="hi e" onClick={() => navigate('/enterprise')}>
            <div className="hi-l">企业治理 E</div>
            <div className="hi-v">
              {helix.e}
              <s>分</s>
            </div>
          </button>
          <button type="button" className="hi w" onClick={() => navigate('/wealth')}>
            <div className="hi-l">财富健康 W</div>
            <div className="hi-v">
              {helix.w}
              <s>分</s>
            </div>
          </button>
        </div>
      </div>

      {/* ---------------- 最紧急的一件事 ---------------- */}
      {topAlert ? (
        <AlertStrip
          tone={topAlert.level === 'danger' ? 'danger' : 'warn'}
          title={topAlert.title}
          sub={`${topAlert.action} · 置信度 ${topAlert.confidence}%`}
          onClick={() => navigate('/health')}
        />
      ) : (
        <AlertStrip tone="ok" title="当前没有未处理预警" sub="系统持续监测 187 项指标，有异常会第一时间提醒您" />
      )}

      {/* ---------------- H-Score 主卡 ---------------- */}
      <Card
        title="H-Score™ 健康资本评分"
        more={
          <Link to="/health" className="card-more">
            详情 ›
          </Link>
        }
      >
        <div className="row between" style={{ alignItems: 'flex-end' }}>
          <div>
            <div className="row-flex" style={{ alignItems: 'baseline', gap: 8 }}>
              <span className="num" style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: resolveColor('h') }}>
                {health.score}
              </span>
              <span className="badge h">{health.grade}</span>
            </div>
            <div className="text-tiny text-muted" style={{ marginTop: 8 }}>
              {health.delta.display} · 近 5 个月趋势
            </div>
          </div>
          <div style={{ width: 150 }}>
            <Chart html={charts.spark({ data: health.trend.map((t) => t.value), w: 300, h: 54, color: 'var(--h)', dots: true })} />
          </div>
        </div>

        <div className="divider" />

        <div className="grid3">
          <div>
            <div className="k-l text-tiny text-muted">生物年龄</div>
            <div className="num" style={{ fontSize: 17, fontWeight: 600 }}>
              {health.bioAge}
              <s style={{ fontSize: 10, opacity: 0.6, marginLeft: 2 }}>岁</s>
            </div>
          </div>
          <div>
            <div className="k-l text-tiny text-muted">实际年龄</div>
            <div className="num" style={{ fontSize: 17, fontWeight: 600 }}>
              {health.realAge}
              <s style={{ fontSize: 10, opacity: 0.6, marginLeft: 2 }}>岁</s>
            </div>
          </div>
          <div>
            <div className="k-l text-tiny text-muted">年轻化</div>
            <div className="num hl-h" style={{ fontSize: 17, fontWeight: 600 }}>
              {health.realAge - health.bioAge}
              <s style={{ fontSize: 10, opacity: 0.6, marginLeft: 2 }}>岁</s>
            </div>
          </div>
        </div>
      </Card>

      {/* ---------------- 今日守护 ---------------- */}
      <Card
        title="今日守护"
        more={
          <span className="card-more">
            {doneCount}/{tasks.length} 已完成
          </span>
        }
      >
        {tasks.length === 0 ? (
          <div className="sec-note">今天没有待执行的守护任务。</div>
        ) : (
          tasks.map((task) => (
            <TaskItem
              key={task.id}
              title={task.title}
              sub={task.sub}
              done={task.done}
              busy={toggleTask.isPending && toggleTask.variables?.id === task.id}
              onToggle={() => toggleTask.mutate({ id: task.id, done: !task.done })}
            />
          ))
        )}
      </Card>

      {/* ---------------- 场景入口 ---------------- */}
      <Card>
        <div className="qi-grid">
          {data.quickEntries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className="qi"
              onClick={() => {
                if (entry.id === 'sos') openSheet('sos');
                else navigate(entry.route);
              }}
            >
              <span
                className="qi-i"
                style={{
                  color: resolveColor(entry.color),
                  background: `color-mix(in srgb, ${resolveColor(entry.color)} 14%, transparent)`,
                  fontSize: 18,
                }}
              >
                {entry.icon}
              </span>
              <span className="qi-l">{entry.label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* ---------------- 专属管家 ---------------- */}
      <div className="guard-card">
        <Avatar char={data.guardian.name.slice(0, 1)} />
        <div className="grow">
          <div className="gc-t">{data.guardian.name}</div>
          <div className="gc-s">
            {data.guardian.title}
            <br />
            {data.guardian.slaText}
          </div>
        </div>
        <button
          type="button"
          className="btn sm"
          onClick={() => {
            window.location.href = `tel:${data.guardian.phone}`;
          }}
        >
          联系
        </button>
      </div>

      <div className="sec-note text-center">
        四级数据授权 · 可随时撤回 · 每次调用全量留痕
        <br />
        {member.levelEn} · {member.memberNo}
      </div>
    </div>
  );
}
